import { Injectable, NgZone, inject, signal } from '@angular/core';
import * as signalR from '@microsoft/signalr';
import { Subject } from 'rxjs';
import { WidgetApi } from '../../data/api/widget.api';
import { WidgetConversation, WidgetMessage } from '../../data/dto/widget.dto';

export interface AgentTypingUpdate {
  readonly conversationId: string;
  readonly isTyping: boolean;
  readonly name?: string;
}

@Injectable({ providedIn: 'root' })
export class WidgetRealtime {
  private readonly api = inject(WidgetApi);
  private readonly zone = inject(NgZone);
  private connection?: signalR.HubConnection;
  private apiUrl = '';
  private conversationId: string | null = null;
  private starting?: Promise<void>;

  readonly connected = signal(false);
  readonly messageCreated = new Subject<WidgetMessage>();
  readonly conversationUpdated = new Subject<WidgetConversation>();
  readonly agentTyping = new Subject<AgentTypingUpdate>();
  readonly closeRequested = new Subject<void>();
  readonly configurationChanged = new Subject<{ enabled: boolean }>();
  readonly availabilityChanged = new Subject<void>();

  configure(apiUrl: string): void { this.apiUrl = apiUrl.replace(/\/$/, ''); }

  async start(): Promise<void> {
    if (this.connection?.state === signalR.HubConnectionState.Connected) return;
    if (this.starting !== undefined) return this.starting;
    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(`${this.apiUrl}/v1/realtime/widgetchat`, {
        accessTokenFactory: () => this.api.getAccessToken(),
        transport: signalR.HttpTransportType.WebSockets,
      })
      .withAutomaticReconnect([0, 1500, 5000, 10000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();
    this.connection.on('MessageCreated', (value: WidgetMessage) => this.zone.run(() => this.messageCreated.next(value)));
    this.connection.on('ConversationUpdated', (value: WidgetConversation) => this.zone.run(() => this.conversationUpdated.next(value)));
    this.connection.on('AgentTyping', (value: AgentTypingUpdate) => this.zone.run(() => this.agentTyping.next(value)));
    this.connection.on('WidgetCloseRequested', () => this.zone.run(() => this.closeRequested.next()));
    this.connection.on('WidgetConfigurationChanged', (value: { enabled: boolean }) =>
      this.zone.run(() => this.configurationChanged.next(value)));
    this.connection.on('WidgetAvailabilityChanged', () => this.zone.run(() => this.availabilityChanged.next()));
    this.connection.onreconnected(() => {
      this.zone.run(() => this.connected.set(true));
      if (this.conversationId) void this.connection?.invoke('JoinConversation', this.conversationId);
    });
    this.connection.onreconnecting(() => this.zone.run(() => this.connected.set(false)));
    this.connection.onclose(() => this.zone.run(() => this.connected.set(false)));
    this.starting = this.connection.start().then(() => this.zone.run(() => this.connected.set(true))).finally(() => { this.starting = undefined; });
    await this.starting;
  }

  async join(conversationId: string): Promise<void> {
    this.conversationId = conversationId;
    await this.start();
    await this.connection?.invoke('JoinConversation', conversationId);
  }

  async updateVisitorDraft(text: string): Promise<void> {
    if (!this.conversationId) return;
    await this.start();
    await this.connection?.invoke('UpdateVisitorDraft', this.conversationId, text);
  }

  async requestClose(): Promise<void> {
    await this.start();
    await this.connection?.invoke('RequestWidgetClose');
  }

  async stop(): Promise<void> {
    this.conversationId = null;
    await this.connection?.stop();
    this.connection = undefined;
    this.connected.set(false);
  }
}
